import { type ComponentPropsWithRef, useEffect, useRef } from 'react'
import cx from 'classnames'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import { NEVER, type Observable } from 'rxjs'
import AutosizeTextarea from 'react-textarea-autosize'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { useFocusTracking, useObservableFocus } from '@/hooks/focus-hooks'
import { match } from '@/utils/pattern-match-util'
import styles from './TextArea.module.css'

export interface TextAreaProps extends Omit<
  ComponentPropsWithRef<typeof AutosizeTextarea>,
  'disabled'
> {
  isDisabled?: boolean
  status?: ControlStatus
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
  value?: string
  rows?: number
  autoSize?: boolean
  focus$?: Observable<unknown>
  isNotValid?: boolean
  /**
   * Focuses and scrolls to the input each time this error appears.
   */
  focusedError?: string
}

/**
 * A multi-line text input, which can grow with its content.
 */
export function TextArea({
  className,
  value,
  onChange,
  rows,
  autoSize,
  status = 'default',
  size: sizeProp,
  focus$ = NEVER,
  onFocus,
  onBlur,
  isNotValid,
  focusedError,
  ref,
  isDisabled,
  ...props
}: TextAreaProps) {
  const size = useControlSize(sizeProp)
  const Element = autoSize ? AutosizeTextarea : 'textarea'
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  useObservableFocus(focus$, inputRef)
  const composedRef = useComposedRefs(ref, inputRef)
  useEffect(() => {
    if (focusedError && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [focusedError])
  const [focusProps, focused] = useFocusTracking({ onFocus, onBlur })
  return (
    <div
      className={cx(
        styles.input,
        match(size, {
          normal: undefined,
          compact: styles.compact,
          small: styles.small,
        }),
        styles[status],
        focused && styles.focused,
        isDisabled && styles.disabled,
        isNotValid && styles.isNotValid,
        className,
      )}
    >
      <Element
        ref={composedRef}
        className={cx(styles.inner)}
        value={value}
        onChange={onChange}
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- rows={0} is invalid, so it falls back too
        rows={rows || 5}
        disabled={isDisabled}
        {...props}
        {...focusProps}
      />
    </div>
  )
}
